import { parseAsBoolean, useQueryStates } from 'nuqs'
import { useCallback, useMemo } from 'react'
import type { AuditTrailStep } from './types'
import { useAuditTrailStoreSelectors } from './use-audit-trail-store'

export function useAuditTrail({ page }: { page?: AuditTrailStep } = {}) {
  const [auditState, setAuditState] = useQueryStates({
    audit: parseAsBoolean.withDefault(false),
  })

  const {
    step,
    setStep,
    setStepSelectionMode,
    reviewerEmails,
    setReviewerEmails,
    searchQuery,
    setSearchQuery,
    disabledMessage,
    setDisabledMessage,
  } = useAuditTrailStoreSelectors()

  const openAuditTrail = useCallback(() => {
    setAuditState((prev) => ({ ...prev, audit: true }))
  }, [setAuditState])

  const closeAuditTrail = useCallback(() => {
    setAuditState((prev) => ({ ...prev, audit: false }))
  }, [setAuditState])

  const handleSetStep = useCallback(
    (step: AuditTrailStep | null) => {
      if (step === page) {
        setStepSelectionMode('auto')
      } else {
        setStepSelectionMode('manual')
      }
      setStep(step)
    },
    [setStep, setStepSelectionMode, page],
  )

  const clearFilters = useCallback(() => {
    handleSetStep(null)
    setReviewerEmails([])
    setSearchQuery('')
  }, [handleSetStep, setReviewerEmails, setSearchQuery])

  const resetToInitialFilters = useCallback(() => {
    setStep(null)
    setStepSelectionMode('auto')
    setReviewerEmails([])
    setSearchQuery('')
  }, [setStep, setStepSelectionMode, setReviewerEmails, setSearchQuery])

  const isDirty = useMemo(() => {
    return step !== null || reviewerEmails.length > 0 || searchQuery !== ''
  }, [step, reviewerEmails, searchQuery])

  return {
    isAuditTrailVisible: auditState.audit,
    openAuditTrail,
    closeAuditTrail,
    step,
    handleSetStep,
    reviewerEmails,
    setReviewerEmails,
    clearFilters,
    isDirty,
    searchQuery,
    setSearchQuery,
    resetToInitialFilters,
    disabledMessage,
    setDisabledMessage,
  }
}
